import {
  type DropTargetDropEvent,
  type DropTargetOptions,
  // IconButton,
  // toast,
  useDropTarget,
} from '@affine/component';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import {
  type FolderNode,
  OrganizeService,
} from '@affine/core/modules/organize';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { isOrganizeSupportType } from '@affine/core/modules/organize/constants';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
// import { AddOrganizeIcon } from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelFolderNode } from '../../nodes/folder';
import { DropEffect, NavigationPanelTreeRoot } from '../../tree';
import {
  organizeChildrenDropEffect,
  organizeEmptyDropEffect,
  organizeRootCanDrop,
} from './dnd';
import { RootEmpty } from './empty';
import { useNewDoc } from '@affine/core/modules/app-sidebar/views';
import { getEnv } from '@affine/core/modules/cloud/constant';

export const NavigationPanelOrganize = () => {
  const { organizeService, navigationPanelService, docDisplayMetaService } =
    useServices({
      OrganizeService,
      NavigationPanelService,
      DocDisplayMetaService,
    });
  const navigationPanelSection = navigationPanelService.sections.organize;
  const collapsed = useLiveData(navigationPanelSection.collapsed$);

  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const createPage = useNewDoc();

  const t = useI18n();

  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const filteredChildren = useLiveData(rootFolder.filteredChildren$);
  const sortedChildren = useLiveData(rootFolder.sortedChildren$);
  const env = useMemo(() => getEnv(), []);

  const folders = env === 'prod' ? filteredChildren : sortedChildren;

  const isLoading = useLiveData(folderTree.isLoading$);

  const handleCreateFolder = useCallback(() => {
    const newFolderId = rootFolder.createFolder(
      'New Folder',
      rootFolder.indexAt('before')
    );
    setNewFolderId(newFolderId);
    navigationPanelSection.setCollapsed(false);
    return newFolderId;
  }, [navigationPanelSection, rootFolder]);

  const handleOnChildrenDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, node?: FolderNode) => {
      if (!node || !node.id) {
        return; // never happens
      }
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        const at =
          data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
        if (data.source.data.entity?.type === 'folder') {
          rootFolder.moveHere(
            data.source.data.entity.id,
            rootFolder.indexAt(at, node.id)
          );
          track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        } else if (
          data.source.data.entity?.type === 'doc' ||
          data.source.data.entity?.type === 'collection' ||
          data.source.data.entity?.type === 'tag'
        ) {
          // 处理 doc/collection/tag 在根级别的重新排序
          if (
            data.source.data.from?.at ===
            'navigation-panel:organize:folder-node'
          ) {
            // 如果是从 organize 内部拖拽，进行移动
            rootFolder.moveHere(
              data.source.data.from.nodeId,
              rootFolder.indexAt(at, node.id)
            );
            track.$.navigationPanel.organize.moveOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          } else {
            // 如果是从外部拖拽，创建新链接
            rootFolder.createLink(
              data.source.data.entity?.type,
              data.source.data.entity.id,
              rootFolder.indexAt(at, node.id)
            );
            track.$.navigationPanel.organize.createOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          }
        }
      } else {
        return; // not supported
      }
    },
    [
      rootFolder,
      // t
    ]
  );

  const createFolderAndDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const newFolderId = handleCreateFolder();
      setNewFolderId(null);
      const newFolder$ = folderTree.folderNode$(newFolderId);

      const entity = data.source.data.entity;
      if (!entity) return;
      const { type, id } = entity;
      if (type !== 'doc' && type !== 'tag' && type !== 'collection') return;

      const folder = newFolder$.value;
      if (!folder) return;
      folder.createLink(type, id, folder.indexAt('after'));
    },
    [folderTree, handleCreateFolder]
  );

  const handleDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const entity = data.source.data.entity;
      if (!entity || !entity.type || !entity.id) {
        return;
      }

      // 检查是否是支持的类型
      if (!isOrganizeSupportType(entity.type)) {
        return;
      }

      if (entity.type === 'folder') {
        // 如果是文件夹，移动到根目录
        rootFolder.moveHere(entity.id, rootFolder.indexAt('before'));
        track.$.navigationPanel.organize.moveOrganizeItem({
          type: 'folder',
        });
      } else if (entity.type === 'doc' || entity.type === 'collection') {
        // 如果是文档、集合或标签，创建链接
        rootFolder.createLink(
          entity.type,
          entity.id,
          rootFolder.indexAt('before')
        );
        track.$.navigationPanel.organize.createOrganizeItem({
          type: 'link',
          target: entity.type,
        });
      }

      // 展开organize面板
      navigationPanelSection.setCollapsed(false);
    },
    [rootFolder, navigationPanelSection]
  );

  const handleChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      const entityType = args.source.data.entity?.type;
      return !!entityType && isOrganizeSupportType(entityType);
    },
    []
  );

  useEffect(() => {
    if (collapsed) setNewFolderId(null); // reset new folder id to clear the renaming state
  }, [collapsed]);

  const { dropTargetRef, draggedOverDraggable, draggedOverPosition } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: {
          at: 'navigation-panel:organize:root',
        },
        onDrop: handleDrop,
        canDrop: organizeRootCanDrop,
        allowExternal: true,
      }),
      [handleDrop]
    );

  return (
    <CollapsibleSection
      name="organize"
      headerRef={dropTargetRef}
      title={t['com.affine.rootAppSidebar.organize']()}
      actions={
        <>
          {/* <IconButton
          data-testid="navigation-panel-bar-add-organize-button"
          onClick={handleCreateFolder}
          size="16"
          tooltip={t[
            'com.affine.rootAppSidebar.explorer.organize-section-add-tooltip'
          ]()}
        >
          <AddOrganizeIcon />
        </IconButton> */}
          {draggedOverDraggable && (
            <DropEffect
              position={draggedOverPosition}
              dropEffect={organizeEmptyDropEffect({
                source: draggedOverDraggable,
                treeInstruction: null,
              })}
            />
          )}
        </>
      }
    >
      <NavigationPanelTreeRoot
        placeholder={
          <RootEmpty
            onClickCreate={createPage}
            isLoading={isLoading}
            onDrop={createFolderAndDrop}
          />
        }
      >
        {folders.map(child => {
          return (
            <NavigationPanelFolderNode
              key={child.id}
              nodeId={child.id as string}
              defaultRenaming={child.id === newFolderId}
              onDrop={handleOnChildrenDrop}
              dropEffect={organizeChildrenDropEffect}
              canDrop={handleChildrenCanDrop}
              location={{
                at: 'navigation-panel:organize:folder-node',
                nodeId: child.id as string,
              }}
            />
          );
        })}
      </NavigationPanelTreeRoot>
    </CollapsibleSection>
  );
};
